import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AppStore {
  // UI State
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  theme: 'light' | 'dark';

  // Search
  globalSearchTerm: string;
  searchHistory: string[];

  // Notifications
  notifications: Notification[];

  // Settings
  settings: {
    itemsPerPage: number;
    currency: string;
    dateFormat: string;
    language: string;
    emailNotifications: boolean;
    desktopNotifications: boolean;
  };

  // Actions
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileMenuOpen: (open: boolean) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setGlobalSearchTerm: (term: string) => void;
  addToSearchHistory: (term: string) => void;
  clearSearchHistory: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'> & { id?: string }) => void;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: () => void;
  updateSettings: (settings: Partial<AppStore['settings']>) => void;

  // Updates
  isUpdateAvailable: boolean;
  setUpdateAvailable: (available: boolean) => void;

  // Lead alerts (nuovi lead in arrivo → pallino sulla voce Lead)
  newLeadCount: number;
  incrementNewLeadCount: (by?: number) => void;
  resetNewLeadCount: () => void;

  // Bozze di fattura degli abbonamenti da emettere → pallino ambra sulla voce Fatture
  subscriptionDraftCount: number;
  setSubscriptionDraftCount: (count: number) => void;

  // Ordini di biglietti arrivati dal sito e non ancora visti → pallino sulla voce Eventi
  newTicketOrderCount: number;
  setNewTicketOrderCount: (count: number) => void;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  timestamp: Date;
  read: boolean;
  persistent?: boolean; // Se true, la notifica non viene auto-dismissata
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
}

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      theme: 'light',
      globalSearchTerm: '',
      searchHistory: [],
      notifications: [],
      settings: {
        itemsPerPage: 25,
        currency: 'EUR',
        dateFormat: 'dd/MM/yyyy',
        language: 'it',
        emailNotifications: true,
        desktopNotifications: true
      },
      isUpdateAvailable: false,
      newLeadCount: 0,
      subscriptionDraftCount: 0,
      newTicketOrderCount: 0,

      // Actions
      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      setMobileMenuOpen: (open) =>
        set({ mobileMenuOpen: open }),

      setTheme: (theme) =>
        set({ theme }),

      setGlobalSearchTerm: (term) =>
        set({ globalSearchTerm: term }),

      addToSearchHistory: (term) =>
        set((state) => {
          const history = [
            term,
            ...state.searchHistory.filter(item => item !== term)
          ].slice(0, 10); // Keep only last 10 searches
          return { searchHistory: history };
        }),

      clearSearchHistory: () =>
        set({ searchHistory: [] }),

      addNotification: (notification) =>
        set((state) => ({
          notifications: [
            {
              ...notification,
              id: notification.id ?? Date.now().toString(),
              timestamp: new Date(),
              read: false
            },
            ...state.notifications
          ].slice(0, 50) // Keep only last 50 notifications
        })),

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter(n => n.id !== id)
        })),

      clearNotifications: () =>
        set({ notifications: [] }),

      markNotificationAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map(n =>
            n.id === id ? { ...n, read: true } : n
          )
        })),

      markAllNotificationsAsRead: () =>
        set((state) => ({
          notifications: state.notifications.map(n => ({ ...n, read: true }))
        })),

      updateSettings: (newSettings) =>
        set((state) => ({
          settings: { ...state.settings, ...newSettings }
        })),

      setUpdateAvailable: (available) =>
        set({ isUpdateAvailable: available }),

      incrementNewLeadCount: (by = 1) =>
        set((state) => ({ newLeadCount: state.newLeadCount + by })),

      resetNewLeadCount: () =>
        set({ newLeadCount: 0 }),

      setSubscriptionDraftCount: (count) =>
        set({ subscriptionDraftCount: count }),

      setNewTicketOrderCount: (count) =>
        set({ newTicketOrderCount: count })
    }),
    {
      name: 'pixel-slab-store',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        searchHistory: state.searchHistory,
        settings: state.settings
        // notifications: ephemeral, non persistere (rischia di "resuscitare" toast non letti ad ogni riavvio)
        // Do not persist isUpdateAvailable
      })
    }
  )
);

// Custom hooks for specific store slices
export const useUI = () => useStore((state) => ({
  sidebarCollapsed: state.sidebarCollapsed,
  mobileMenuOpen: state.mobileMenuOpen,
  theme: state.theme,
  setSidebarCollapsed: state.setSidebarCollapsed,
  setMobileMenuOpen: state.setMobileMenuOpen,
  setTheme: state.setTheme
}));

export const useSearch = () => useStore((state) => ({
  globalSearchTerm: state.globalSearchTerm,
  searchHistory: state.searchHistory,
  setGlobalSearchTerm: state.setGlobalSearchTerm,
  addToSearchHistory: state.addToSearchHistory,
  clearSearchHistory: state.clearSearchHistory
}));

export const useNotifications = () => useStore((state) => ({
  notifications: state.notifications,
  addNotification: state.addNotification,
  removeNotification: state.removeNotification,
  clearNotifications: state.clearNotifications,
  markNotificationAsRead: state.markNotificationAsRead,
  markAllNotificationsAsRead: state.markAllNotificationsAsRead
}));

export const useSettings = () => useStore((state) => ({
  settings: state.settings,
  updateSettings: state.updateSettings
}));

export const useLeadAlerts = () => useStore((state) => ({
  newLeadCount: state.newLeadCount,
  incrementNewLeadCount: state.incrementNewLeadCount,
  resetNewLeadCount: state.resetNewLeadCount
}));